import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mail, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import PortalFooter from '../components/PortalFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  
  // State
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      // Read response as text first, then parse - avoids clone/body-read issues
      let data;
      try {
        const responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (jsonError) {
        console.error('Response parsing error:', jsonError);
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }
      
      setSuccess(true);
      
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8EEFF] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <a href="https://losspay.com" className="flex-shrink-0">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-8 md:h-9"
              />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          
          {/* Badge */}
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-sm font-medium text-[#3B5BDB]">
              <span className="w-2 h-2 bg-[#3B5BDB] rounded-full"></span>
              Password Recovery
            </span>
          </div>

          {success ? (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Check Your <span className="text-[#3B5BDB]">Email</span>
                </h2>
                <p className="text-slate-600 mb-6">
                  If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
                </p>
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">
                    Didn't receive the email? Check your spam folder or
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSuccess(false)}
                    className="w-full rounded-xl border-slate-200"
                  >
                    Try Again
                  </Button>
                  <Link 
                    to="/portal/login"
                    className="block text-center text-[#3B5BDB] hover:underline text-sm font-semibold"
                  >
                    Back to Login
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#E8EEFF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-[#3B5BDB]" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    Forgot <span className="text-[#3B5BDB]">Password?</span>
                  </h1>
                  <p className="text-slate-600">
                    No worries! Enter your email and we'll send you a reset link.
                  </p>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="h-12 pl-12 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl font-semibold text-base"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending Reset Link...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <Link 
                    to="/portal/login"
                    className="inline-flex items-center text-[#3B5BDB] hover:underline text-sm font-semibold"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Login
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Security Note */}
          <p className="text-xs text-slate-500 text-center mt-6 px-4">
            Your information is protected using secure encryption and industry standard safeguards.
          </p>
          
        </div>
      </main>

      {/* Footer */}
      <PortalFooter />
    </div>
  );
}
